if (!customElements.get('njieli-pwyw')) {
  customElements.define(
    'njieli-pwyw',
    class NjieliPwyw extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        try {
          this.config = JSON.parse(this.querySelector('[data-njieli-pwyw-config]').textContent);
        } catch (error) {
          console.error('NJIELI PWYW: Konfiguration fehlt', error);
          return;
        }

        this.amountInput = this.querySelector('[data-amount]');
        this.summary = this.querySelector('[data-summary]');
        this.errorEl = this.querySelector('[data-error]');
        this.typeInputs = Array.from(this.querySelectorAll('[data-purchase-type]'));
        this.planSelect = this.querySelector('[data-plan-select]');
        this.intervalEl = this.querySelector('[data-interval]');
        this.priceOnceEl = this.querySelector('[data-price-once]');
        this.priceSubEl = this.querySelector('[data-price-sub]');
        this.variantId = this.readVariantId();
        this.submitting = false;

        this.amountInput.addEventListener('input', () => this.update());
        this.amountInput.addEventListener('blur', () => this.clampAmount());
        this.querySelectorAll('[data-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const next = this.getAmount() + Number(button.dataset.step);
            this.amountInput.value = Math.max(this.getMinimum(), next);
            this.update();
          });
        });
        this.typeInputs.forEach((input) => input.addEventListener('change', () => this.update()));
        if (this.planSelect) this.planSelect.addEventListener('change', () => this.update());

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            setTimeout(() => this.syncVariant(), 0);
          });
        }
        this.onDocumentChange = () => setTimeout(() => this.syncVariant(), 0);
        document.addEventListener('change', this.onDocumentChange);

        this.onSubmit = this.handleSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        this.update();
      }

      disconnectedCallback() {
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.onDocumentChange) document.removeEventListener('change', this.onDocumentChange);
        if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
        this.initialized = false;
      }

      getForm() {
        const scope = this.closest('section, .shopify-section') || document;
        return (
          scope.querySelector('form[action*="/cart/add"][data-type="add-to-cart-form"]') ||
          scope.querySelector('form[action*="/cart/add"]') ||
          document.querySelector('form[action*="/cart/add"]')
        );
      }

      readVariantId() {
        const form = this.getForm();
        const input = form && form.querySelector('[name="id"]');
        return input ? String(input.value) : String(Object.keys(this.config.variants)[0]);
      }

      getVariant() {
        return this.config.variants[this.variantId] || null;
      }

      isSubscription() {
        const checked = this.typeInputs.find((input) => input.checked);
        return Boolean(checked && checked.value === 'subscription');
      }

      getPlanId() {
        if (!this.isSubscription() || !this.planSelect) return null;
        return String(this.planSelect.value);
      }

      getMinimum() {
        const variant = this.getVariant();
        return variant ? Math.ceil(variant.price / 100) : 1;
      }

      getAmount() {
        const value = parseInt(this.amountInput.value, 10);
        return Number.isFinite(value) ? value : 0;
      }

      canAddExtraForPlan(planId) {
        if (!planId) return Boolean(this.config.pwywVariantId);
        return Boolean(this.config.pwywVariantId && this.config.pwywPlans[planId] != null);
      }

      formatMoney(cents) {
        const hasCents = Math.round(cents) % 100 !== 0;
        return new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      }

      calculate() {
        const variant = this.getVariant();
        const planId = this.getPlanId();
        const minimum = this.getMinimum();
        const amount = Math.max(this.getAmount(), minimum);
        const extraUnits = this.canAddExtraForPlan(planId) ? amount - minimum : 0;

        let mainPrice = variant ? variant.price : minimum * 100;
        let unitPrice = 100;
        if (planId && variant && variant.plans[planId] != null) {
          mainPrice = variant.plans[planId];
          unitPrice = this.config.pwywPlans[planId] != null ? this.config.pwywPlans[planId] : 100;
        }

        return {
          planId,
          minimum,
          amount: minimum + extraUnits,
          extraUnits,
          total: mainPrice + extraUnits * unitPrice,
        };
      }

      syncVariant() {
        const nextId = this.readVariantId();
        if (nextId === this.variantId) return;
        this.variantId = nextId;
        this.amountInput.value = this.getMinimum();
        this.update();
      }

      clampAmount() {
        if (this.getAmount() < this.getMinimum()) this.amountInput.value = this.getMinimum();
        this.update();
      }

      update() {
        const variant = this.getVariant();
        const minimum = this.getMinimum();
        const subscription = this.isSubscription();
        const planId = this.getPlanId();

        this.amountInput.min = minimum;
        if (this.intervalEl) this.intervalEl.hidden = !subscription;
        this.hideError();

        if (variant) {
          if (this.priceOnceEl) this.priceOnceEl.textContent = this.formatMoney(variant.price);
          if (this.priceSubEl) {
            const firstPlan = this.planSelect ? this.planSelect.value : null;
            const subPrice = firstPlan && variant.plans[firstPlan] != null ? variant.plans[firstPlan] : variant.price;
            this.priceSubEl.textContent = this.formatMoney(subPrice);
          }
        }

        const allowExtra = this.canAddExtraForPlan(planId);
        this.amountInput.disabled = !allowExtra;
        this.querySelectorAll('[data-step]').forEach((button) => (button.disabled = !allowExtra));
        if (!allowExtra) this.amountInput.value = minimum;

        const amount = this.getAmount();
        if (amount < minimum) {
          this.summary.textContent = `Der Mindestpreis für diese Variante ist ${this.formatMoney(minimum * 100)}.`;
          return;
        }

        const result = this.calculate();
        const extra = result.amount - result.minimum;

        if (subscription) {
          const intervalText =
            this.planSelect && this.planSelect.selectedOptions[0]
              ? ` alle ${this.planSelect.selectedOptions[0].textContent.trim()}`
              : '';
          const savings = result.amount * 100 - result.total;
          let text = `Du zahlst ${this.formatMoney(result.total)} pro Lieferung${intervalText}`;
          if (savings > 0) text += ` – dein Wunschpreis von ${this.formatMoney(result.amount * 100)} abzüglich Abo-Vorteil.`;
          else text += '.';
          this.summary.textContent = text;
        } else if (extra > 0) {
          this.summary.textContent = `Du zahlst ${this.formatMoney(result.total)} – davon ${this.formatMoney(extra * 100)} freiwillig über dem regulären Preis.`;
        } else {
          this.summary.textContent = `Du zahlst den regulären Preis von ${this.formatMoney(result.total)}.`;
        }
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      collectProperties(form) {
        const properties = {};
        new FormData(form).forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
        });
        return properties;
      }

      async handleSubmit(event) {
        const form = event.target;
        if (!form.matches || form !== this.getForm()) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (this.submitting) return;

        this.variantId = this.readVariantId();
        const minimum = this.getMinimum();
        if (this.getAmount() < minimum) {
          this.showError(`Bitte gib mindestens ${this.formatMoney(minimum * 100)} ein.`);
          this.amountInput.focus();
          return;
        }

        const result = this.calculate();
        const variant = this.getVariant();
        const quantityInput = form.querySelector('[name="quantity"]');
        const quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);

        const mainItem = { id: Number(this.variantId), quantity };
        const properties = this.collectProperties(form);
        if (Object.keys(properties).length) mainItem.properties = properties;
        if (result.planId) mainItem.selling_plan = Number(result.planId);

        const items = [mainItem];
        if (result.extraUnits > 0) {
          const extraItem = {
            id: Number(this.config.pwywVariantId),
            quantity: result.extraUnits * quantity,
            properties: {
              '_Wunschpreis': this.formatMoney(result.amount * 100),
              '_Zugehöriges Produkt': this.config.productTitle,
              '_Gewählte Variante': variant ? variant.title : '',
            },
          };
          if (result.planId) extraItem.selling_plan = Number(result.planId);
          items.unshift(extraItem);
        }

        const submitButton = form.querySelector('[type="submit"]');
        this.submitting = true;
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');
        }

        try {
          const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Unbekannter Fehler');
          }
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        } catch (error) {
          console.error('NJIELI PWYW:', error);
          this.showError(`Das hat leider nicht geklappt: ${error.message}`);
          this.submitting = false;
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }
        }
      }
    }
  );
}
